package meineglasfaser.api.ubl

import com.ionspin.kotlin.bignum.decimal.BigDecimal
import com.ionspin.kotlin.bignum.decimal.RoundingMode
import kotlinx.serialization.Contextual
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import meineglasfaser.api.Currency

interface UBLAmountLike {
    fun displayStr(): String

    fun displayStrCurrency(): String
}

@Serializable
data class UBLAmount(
    @SerialName("currency_id")
    val currency: Currency,
    @Contextual
    @SerialName("value")
    val value: BigDecimal,
) : UBLAmountLike {
    operator fun plus(other: UBLAmount): UBLAmount {
        require(currency == other.currency) {
            "Cannot add amounts with different currencies: $currency != ${other.currency}"
        }
        return UBLAmount(
            currency = currency,
            value = value + other.value,
        )
    }

    operator fun times(factor: Int): UBLAmount {
        return UBLAmount(
            currency = currency,
            value = value * BigDecimal.fromInt(factor),
        )
    }

    operator fun times(factor: BigDecimal): UBLAmount {
        return UBLAmount(
            currency = currency,
            value = value * factor,
        )
    }

    fun withTax(taxCategory: UBLTaxCategory): UBLAmount {
        return UBLAmount(
            currency = currency,
            value = value * (BigDecimal.ONE + taxCategory.percent / BigDecimal.fromInt(100)),
        )
    }

    override fun displayStr(): String {
        val minorUnit = currency.minorUnit ?: 2
        val roundedValue = value.roundToDigitPositionAfterDecimalPoint(
            digitPosition = minorUnit.toLong(),
            roundingMode = RoundingMode.ROUND_HALF_AWAY_FROM_ZERO,
        )
        val str = roundedValue.toStringExpanded()
        val parts = str.split(".")
        val integerPart = parts[0]
        val decimalPart = if (parts.size > 1) parts[1] else ""
        val paddedDecimal = decimalPart.padEnd(minorUnit, '0').take(minorUnit)
        return if (minorUnit > 0) "$integerPart,$paddedDecimal" else integerPart
    }

    override fun displayStrCurrency(): String {
        return "${currency.displaySymbol} ${displayStr()}"
    }

    companion object {
        val ZERO_EUR = UBLAmount(
            currency = Currency.EUR,
            value = BigDecimal.ZERO,
        )

        val ONE_EUR = UBLAmount(
            currency = Currency.EUR,
            value = BigDecimal.ONE,
        )

        fun eur(value: String): UBLAmount {
            return UBLAmount(
                currency = Currency.EUR,
                value = BigDecimal.parseString(value.replace(",", ".")),
            )
        }
    }
}
